import React, { Dispatch, ReactElement, SetStateAction } from 'react';
import { Text } from '@sede-enterprise/shell-ds-react-framework';
import tableEditOptions from '../../../api/TableEditOptions';
import {
  I_ALIGNE_TABLE,
  I_GROUP_TABLE,
  ENDUR_I_GAS_TABLE,
  NUCLEUS_I_POWER_TABLE,
  ENDUR_SEAM_REGION_SCOPE_TABLE,
  NUCLEUS_SEAM_REGION_SCOPE_TABLE,
  COUNTRY_CODE,
  COUNTRY_NAME,
  OUT_OF_SCOPE,
  IN_SCOPE,
  FIVE_THOUSAND,
  THOUSAND,
  ELEVEN,
  TWENTY_SIX,
  COMMENTS,
  SCOPE,
  BUYSELL,
  DEAL_QUANTITY,
  MARKET,
  NCF_CATEGORY,
  QTY,
  ID,
  ASIA_JAPAN_SHORT_NAME,
  ENDURAU_STATIC_MAP_NCF,
  SEPH_STATIC_MAP_NCF_TABLE,
  VOLUME,
  NCF_SCOPE,
  ASIA_INDIA_SHORT_NAME,
} from '../../../api/constants';
import {
  fetchCountryForSeamRegionTableEdit,
  fetchMarketOptions,
  fetchNcfCategoriesForAsiaAndAmericaRegion,
  fetchNcfCategoriesForAsiaAndAmericaRegionV1,
  fetchNcfCategoriesOptions,
  fetchReport,
} from '../../../api/services';
import { showSpinner, hideSpinner, globalApiErrorHandler } from '../../Utils/utils';
import {
  Action,
  IAuditPostData,
  ICountry,
  IEditDirtyData,
  IEditFeatureData,
  IEditTableData,
  IEditTableResetOptions,
  IEditTableResetOptionsWithState,
  IPublishData,
  OptionI,
} from '../../../Types/types';
import { AxiosResponse } from 'axios';
import { ERROR_FETCHING_PPT_DATA } from '../../../../CarbonDashboard/constants';

// Function setEditableColumnNames - set editable columns for selected table
const setEditableColumnNames = (
  state: IEditFeatureData,
  dispatch: Dispatch<Action>,
  cols: string[],
) => {
  const editableColumn =
    state.tableSelected !== ''
      ? (
          tableEditOptions[state.tableSelected as keyof typeof tableEditOptions] as {
            editableFields: string[];
          }
        ).editableFields
      : [];
  //console statement
  const colEdit = [];
  for (const editColumn of editableColumn) {
    for (const col of cols) {
      const editableColumnVal = editColumn.toLowerCase().replace('_', '');
      const colsVal = col.toLowerCase().replace('_', '');
      if (editableColumnVal === colsVal) {
        colEdit.push(col);
      }
    }
  }
  //console statement
  dispatch({ type: 'setEditableCol', payload: colEdit });
};

// Function handleOnChange - to fetch pagination data on page chages in the table
export const handleOnChange = async (
  current: number,
  dispatch: Dispatch<Action>,
  state: IEditFeatureData,
  isEdit: boolean,
  isReadyToPublish: boolean,
  ID1: string,
): Promise<void> => {
  dispatch({ type: 'setCurrentPaginationNum', payload: current });
  showSpinner(dispatch);

  const options = { ...state.filteredOptions, pageNumber: current };

  try {
    const viewReport = await fetchReport('get', options);
    if (viewReport?.data) {
      const masterPaginationData = JSON.parse(JSON.stringify(viewReport?.data?.records));
      dispatch({
        type: 'setMasterPaginationData',
        payload: masterPaginationData,
      });
      hideSpinner(dispatch);
      setUpPagination(isEdit, isReadyToPublish, viewReport, dispatch, state, ID1);
    }
  } catch (e) {
    globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, e);
  }
};
//END of Pagination

const setViewEditPaginationData = (data: IEditTableData[], dispatch: Dispatch<Action>) => {
  dispatch({ type: 'setViewEditPaginationData', payload: data });
};

const setUpPagination = (
  isEdit: boolean,
  isReadyToPublish: boolean,
  viewReport: AxiosResponse,
  dispatch: Dispatch<Action>,
  state: IEditFeatureData,
  ID1: string,
) => {
  if (!isEdit && !isReadyToPublish) {
    // Set pagination data from pagination controls during non-Edit mode
    setViewEditPaginationData(viewReport.data.records, dispatch);
    return;
  }
  const records = viewReport.data.records;
  //for edit flow , whenever user has changed data on different pages and user goes to that page
  //compare api response with dirty data and restore the changes user had done previously
  //so that the view will have the changes user had done
  for (const dirtyData of state.dirtyData) {
    const matchedRecord = records.find((record: IEditTableData) => dirtyData[ID1] === record[ID1]);
    if (matchedRecord) {
      const dirtyKeys = Object.keys(dirtyData).slice(1);
      for (const key of dirtyKeys) {
        matchedRecord[key] = dirtyData[key];
      }
    }
  }
  setViewEditPaginationData(records, dispatch);
};

export const getColumnNames = (cols: string[]) => {
  const IdColWithoutSplChar = ID.toLowerCase().replace('_', '');
  const QtyColWithoutSplChar = QTY.toLowerCase().replace('_', '');
  const DealQtyColWithoutSplChar = DEAL_QUANTITY.toLowerCase().replace('_', '');
  const MarketColWithoutSplChar = MARKET.toLowerCase().replace('_', '');
  const NcfCtgryColWithoutSplChar = NCF_CATEGORY.toLowerCase().replace('_', '');
  const BuySellColWithoutSplChar = BUYSELL.toLowerCase().replace('_', '');
  const CommentsColWithoutSplChar = COMMENTS.toLowerCase().replace('_', '');
  const ScopeColWithoutSplChar = SCOPE.toLowerCase().replace('_', '');
  const VolumeNameWithoutSplChar = VOLUME.toLowerCase().replace('_', '');
  const NcfScopeWithoutSplChar = NCF_SCOPE.toLowerCase().replace('_', '');
  const CountryNameWithoutSplChar = COUNTRY_NAME ? COUNTRY_NAME.toLowerCase().replace('_', '') : '';
  const columnMappings: { [key: string]: string } = {
    [IdColWithoutSplChar]: 'ID1',
    [QtyColWithoutSplChar]: 'QTY1',
    [DealQtyColWithoutSplChar]: 'DEAL_QUANTITY1',
    [MarketColWithoutSplChar]: 'MARKET1',
    [NcfCtgryColWithoutSplChar]: 'NCF_CATEGORY1',
    [BuySellColWithoutSplChar]: 'BUYSELL1',
    [CommentsColWithoutSplChar]: 'COMMENTS1',
    [ScopeColWithoutSplChar]: 'SCOPE1',
    [CountryNameWithoutSplChar]: 'COUNTRY_NAME1',
    [VolumeNameWithoutSplChar]: 'VOLUME1',
    [NcfScopeWithoutSplChar]: 'NCF_SCOPE1',
  };

  const result: { [key: string]: string } = {};

  for (const col of cols) {
    const colNameWithoutSpecialChars = col.toLowerCase().replace('_', '');

    if (columnMappings[colNameWithoutSpecialChars]) {
      result[columnMappings[colNameWithoutSpecialChars]] = col;
    }
  }

  // Ensure all keys have a value, defaulting to an empty string
  for (const key in columnMappings) {
    result[columnMappings[key]] ??= '';
  }

  return result;
};

const setUpEditFeatureForAustraliaTables = async (
  state: { tableSelected: string },
  dispatch: Dispatch<Action>,
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>,
) => {
  const ncfCatOption: OptionI[] = [];
  showSpinner(dispatch);
  try {
    let ncfCat;
    if (state.tableSelected === ENDURAU_STATIC_MAP_NCF) {
      ncfCat = await fetchNcfCategoriesForAsiaAndAmericaRegion('AU');
    } else if (state.tableSelected === SEPH_STATIC_MAP_NCF_TABLE) {
      ncfCat = await fetchNcfCategoriesForAsiaAndAmericaRegion('AS-PH');
    }

    const ncfCatOptns = Array.from(
      new Set(ncfCat?.data.map((item: { NCF_CATEGORY: string }) => item.NCF_CATEGORY)),
    ).sort((a, b) => {
      // Explicitly cast a and b to string
      const stringA = a as string;
      const stringB = b as string;
      // Compare function to sort elements by their natural order
      if (stringA < stringB) {
        return -1;
      }
      if (stringA > stringB) {
        return 1;
      }
      return 0;
    });
    for (const ncfCatOpt of ncfCatOptns) {
      ncfCatOption.push({ value: ncfCatOpt as string, label: ncfCatOpt as string });
    }

    dispatch({ type: 'setNcfCategoryOptions', payload: ncfCatOption });
  } catch (error) {
    globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, error);
    setIsServiceFailure(true);
  }
  hideSpinner(dispatch);
};

const setUpEditFeatureForEuropeTables = async (
  state: { tableSelected: string },
  dispatch: Dispatch<Action>,
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>,
) => {
  if ([I_ALIGNE_TABLE, I_GROUP_TABLE].includes(state.tableSelected)) {
    const marketOption = [];
    const ncfCatOption = [];
    showSpinner(dispatch);
    try {
      const market = await fetchMarketOptions(state.tableSelected);
      const ncfCat = await fetchNcfCategoriesOptions(state.tableSelected);
      const ncfCatOptns = ncfCat?.data;
      const marketOptns = market?.data;
      for (const ncfCatOpt of ncfCatOptns) {
        ncfCatOption.push({ value: ncfCatOpt, label: ncfCatOpt });
      }
      for (const marketOpt of marketOptns) {
        marketOption.push({ value: marketOpt, label: marketOpt });
      }
      dispatch({ type: 'setMarketOptions', payload: marketOption });
      dispatch({ type: 'setNcfCategoryOptions', payload: ncfCatOption });
    } catch (error) {
      globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, error);
      setIsServiceFailure(true);
    }
    hideSpinner(dispatch);
  }
};

const setUpEditFeatureForEndureIgasAndNucleusIpowerTables = async (
  state: IEditFeatureData,
  dispatch: Dispatch<Action>,
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>,
) => {
  //fetch ncf cat options for the below tables.
  if ([ENDUR_I_GAS_TABLE, NUCLEUS_I_POWER_TABLE].includes(state.tableSelected)) {
    const ncfCatOption = [];
    showSpinner(dispatch);
    try {
      const ncfCat = await fetchNcfCategoriesOptions(state.tableSelected);
      const ncfCatOptns = ncfCat?.data;
      for (const ncfCatOpt of ncfCatOptns) {
        ncfCatOption.push({ value: ncfCatOpt, label: ncfCatOpt });
      }
      dispatch({ type: 'setNcfCategoryOptions', payload: ncfCatOption });
    } catch (error) {
      globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, error);
      setIsServiceFailure(true);
    }
    hideSpinner(dispatch);
  }
};

const setUpEditFeatureForAmericasSeamRegionScopeTables = async (
  state: IEditFeatureData,
  dispatch: Dispatch<Action>,
  setRegionOptionsForAmericasSeamRegionTable: Dispatch<SetStateAction<ICountry[]>>,
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>,
) => {
  if (
    [ENDUR_SEAM_REGION_SCOPE_TABLE, NUCLEUS_SEAM_REGION_SCOPE_TABLE].includes(state.tableSelected)
  ) {
    showSpinner(dispatch);
    try {
      const regionForSeamRegionTableEditResp = await fetchCountryForSeamRegionTableEdit('Americas');
      const regionForSeamRegionTableEditOptions = regionForSeamRegionTableEditResp?.data;
      setRegionOptionsForAmericasSeamRegionTable(regionForSeamRegionTableEditOptions);
    } catch (error) {
      globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, error);
      setIsServiceFailure(true);
    }
    hideSpinner(dispatch);
  }
};

const setUpEditFeatureForAsiaJapanTables = async (
  state: IEditFeatureData,
  dispatch: Dispatch<Action>,
  setNcfCatOptionsForAsiaJapanTable: Dispatch<SetStateAction<OptionI[]>>,
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>,
) => {
  if (state.filteredRegion.toUpperCase() === ASIA_JAPAN_SHORT_NAME) {
    const regionForAsiaJapanTableEditOptns: OptionI[] = [];
    showSpinner(dispatch);
    try {
      const regionForAsiaJapanTablesResp = await fetchNcfCategoriesForAsiaAndAmericaRegionV1(
        state.filteredRegion,
      );
      const regionForAsiaJapanTableEditOptions = regionForAsiaJapanTablesResp?.data ?? []
        .map((item: { NCF_CATEGORY: string }) => item.NCF_CATEGORY)
        .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);
      for (const tableEditOption of regionForAsiaJapanTableEditOptions) {
        regionForAsiaJapanTableEditOptns.push({
          label: tableEditOption,
          value: tableEditOption,
        });
      }
      setNcfCatOptionsForAsiaJapanTable(regionForAsiaJapanTableEditOptns);
    } catch (error) {
      globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, error);
      setIsServiceFailure(true);
    }
    hideSpinner(dispatch);
  }
};

const setUpEditFeatureForAsiaIndiaTables = async (
  state: IEditFeatureData,
  dispatch: Dispatch<Action>,
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>,
) => {
  if (state.filteredRegion.toUpperCase() === ASIA_INDIA_SHORT_NAME) {
    showSpinner(dispatch);
    try {
      const regionForAsiaIndiaTablesResp = await fetchNcfCategoriesOptions(state.tableSelected);
      const regionForAsiaIndiaTableEditOptions = regionForAsiaIndiaTablesResp?.data
        .filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);

      const regionForAsiaIndiaTableEditOptns = regionForAsiaIndiaTableEditOptions.map((option: string) => ({
        label: option,
        value: option,
      }));
      dispatch({ type: 'setNcfCategoryOptions', payload: regionForAsiaIndiaTableEditOptns });
    } catch (error) {
      globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, error);
      setIsServiceFailure(true);
    }
    hideSpinner(dispatch);
  }
};

// Function handleEdit - is called whenever an editable field is modified
export const handleEdit = async (options: {
  setNcfCatOptionsForAsiaJapanTable: Dispatch<SetStateAction<OptionI[]>>;
  setRegionOptionsForAmericasSeamRegionTable: Dispatch<SetStateAction<ICountry[]>>;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
  setIsShowEditBtn: Dispatch<SetStateAction<boolean>>;
  state: IEditFeatureData;
  dispatch: Dispatch<Action>;
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>;
  cols: string[];
}) => {
  const {
    setNcfCatOptionsForAsiaJapanTable,
    setRegionOptionsForAmericasSeamRegionTable,
    setIsEdit,
    setIsShowEditBtn,
    state,
    dispatch,
    setIsServiceFailure,
    cols,
  } = options;
  //setting up edit feature for different tables
  // setting up edit feature of Europe tables
  setUpEditFeatureForEuropeTables(state, dispatch, setIsServiceFailure);
  // setting up edit feature of Endur I-Gas and Nucleus I-Power tables
  setUpEditFeatureForEndureIgasAndNucleusIpowerTables(state, dispatch, setIsServiceFailure);
  // setting up edit feature of Americas Seam Region Scope tables
  setUpEditFeatureForAmericasSeamRegionScopeTables(
    state,
    dispatch,
    setRegionOptionsForAmericasSeamRegionTable,
    setIsServiceFailure,
  );
  // setting up edit feature of Asia Japan tables
  setUpEditFeatureForAsiaJapanTables(
    state,
    dispatch,
    setNcfCatOptionsForAsiaJapanTable,
    setIsServiceFailure,
  );
  // setting up edit feature of Asia India tables
  setUpEditFeatureForAsiaIndiaTables(
    state,
    dispatch,
    setIsServiceFailure,
  );
  // setting up edit feature of Australia tables and Asia Phillipines Static Map NCF table
  if (
    state.tableSelected === ENDURAU_STATIC_MAP_NCF ||
    state.tableSelected === SEPH_STATIC_MAP_NCF_TABLE
  ) {
    setUpEditFeatureForAustraliaTables(state, dispatch, setIsServiceFailure);
  }

  setEditableColumnNames(state, dispatch, cols);
  setIsEdit(true);
  setIsShowEditBtn(false);
};

export const isQtyValid = (value: number | string) => {
  let isQtyWholeNumberUpto25Digits = true;
  let isQtyDecimalUpto10Digits = true;
  let isQtyExceedsMaxLength = false;
  const isQtyEmpty = value === '' || value === null || value === undefined;
  const pattern = /^-?\d{1,25}(\.\d{1,10})?$/;

  if (typeof value === 'number') {
    isQtyExceedsMaxLength = !pattern.test(value.toString());
  } else if (isQtyEmpty) {
    return false;
  } else {
    isQtyWholeNumberUpto25Digits = value.split('.')[0].length < TWENTY_SIX;
    isQtyDecimalUpto10Digits = value.split('.')[1] ? value.split('.')[1].length < ELEVEN : true;
  }
  const isQtyInvalid =
    !isQtyWholeNumberUpto25Digits || !isQtyDecimalUpto10Digits || isQtyExceedsMaxLength;

  return !isQtyInvalid;
};

// Function handleValAndSave - is called when button 'Validate And Save' is clicked
export const handleValAndSave = (handleValAndSaveOptions: {
  state: IEditFeatureData;
  isEdit: boolean;
  isReadyToPublish: boolean;
  ID1: string;
  setIsDirtyData: Dispatch<React.SetStateAction<boolean>>;
  setErrorTableData: Dispatch<React.SetStateAction<IEditTableData[]>>;
  isDirtyData: boolean;
  COMMENTS1: string;
  setIsReadyToPublish: Dispatch<React.SetStateAction<boolean>>;
  dispatch: Dispatch<Action>;
  setIsEditError: Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: Dispatch<React.SetStateAction<boolean>>;
  setIsShowPublishBtn: Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    state,
    isEdit,
    isReadyToPublish,
    ID1,
    setIsDirtyData,
    setErrorTableData,
    isDirtyData,
    COMMENTS1,
    setIsReadyToPublish,
    dispatch,
    setIsEditError,
    setIsEdit,
    setIsShowPublishBtn,
  } = handleValAndSaveOptions;
  let isValidData = true;
  let isdirtyData = isDirtyData;
  const currDirtyData = [...state.dirtyData];
  const currErrData = [...state.erroredRows];

  // check if any error data still present when HANDLE and SAVE is clicked.
  isValidData = currErrData.length === 0;

  // check if any UI error, if so throw respective error messages
  handleErrorMessageScenarios(state, dispatch);
  //check dirtyData must have COMMENTS column for all the dirty row entries
  if (isValidData) {
    // check this only if validation is true. If validation is already false, no need to check as the error mesasge will already be thrown
    for (const data of currDirtyData) {
      //if COMMENTS is not present, validation will fail and break out of the for loop
      if (!Object.hasOwn(data, COMMENTS1)) {
        isValidData = false;
        break;
      }
      isValidData = true;
    }
  }
  isdirtyData = currDirtyData.length > 0;

  if (!isdirtyData) {
    // if no dirty data at all
    setIsDirtyData(isdirtyData);

    if (state.publishRows.length > 0) {
      dispatch({ type: 'setViewEditPaginationData', payload: state.publishRows });
      setIsReadyToPublish(true);
    } else {
      handleOnChange(state.currentPaginationNum, dispatch, state, isEdit, isReadyToPublish, ID1);
      setIsEditError(false);
      setIsReadyToPublish(false);
    }
  } else if (isdirtyData && isValidData) {
    //show updated table data and clear all dirty data as all data is finalized to be published and no dirty data left.
    dispatch({ type: 'setViewEditPaginationData', payload: state.publishRows });
    setIsEditError(false);
    setIsEdit(false);
    setIsShowPublishBtn(true);
    //setting mode to Publish
    setIsReadyToPublish(true);
  } else {
    //show errored columns only
    dispatch({ type: 'setViewEditPaginationData', payload: currErrData });
    setErrorTableData(currErrData);
    setIsEditError(true);
  }
};

const handleErrorMessageScenarios = (state: IEditFeatureData, dispatch: Dispatch<Action>) => {
  const currErrData = [...state.erroredRows];
  const masterErroredData = [...state.masterErroredRows];
  const differences: {
    Id: number;
    columnName: string[];
    isCommentsValid: boolean;
    isQuantityValid: boolean;
    isCountryValid: boolean;
  }[] = [];
  const colNames = getColumnNames(Object.keys(masterErroredData[0]));
  const {
    ID1,
    QTY1,
    DEAL_QUANTITY1,
    MARKET1,
    NCF_CATEGORY1,
    BUYSELL1,
    COMMENTS1,
    SCOPE1,
    COUNTRY_NAME1,
    VOLUME1,
    NCF_SCOPE1,
  } = colNames;
  const editableColumns = [
    QTY1,
    DEAL_QUANTITY1,
    MARKET1,
    NCF_CATEGORY1,
    BUYSELL1,
    SCOPE1,
    COUNTRY_NAME1,
    VOLUME1,
    NCF_SCOPE1,
  ];
  for (const masterData of masterErroredData) {
    for (const erroredRow of currErrData) {
      processErroredData(
        masterData,
        erroredRow,
        {
          ID1,
          COMMENTS1,
          COUNTRY_NAME1,
          SCOPE1,
          QTY1,
          DEAL_QUANTITY1,
          VOLUME1,
          NCF_SCOPE1,
        },
        editableColumns,
        differences,
      );
    }
  }
  dispatch({
    type: 'setIsAnyInvalidComment',
    payload: differences.some((data) => !data.isCommentsValid),
  });
  dispatch({
    type: 'setIsAnyInvalidCountryName',
    payload: differences.some((data) => !data.isCountryValid),
  });
  dispatch({
    type: 'setIsAnyInvalidQuantity',
    payload: differences.some((data) => !data.isQuantityValid),
  });
};

const processErroredData = (
  masterData: IEditTableData,
  erroredRow: IEditTableData,
  colNames: {
    ID1: string;
    COMMENTS1: string;
    COUNTRY_NAME1: string;
    SCOPE1: string;
    QTY1: string;
    DEAL_QUANTITY1: string;
    VOLUME1: string;
    NCF_SCOPE1: string;
  },
  editableColumns: string | string[],
  differences: {
    Id: string | number;
    columnName: string[];
    isCommentsValid: boolean;
    isQuantityValid: boolean;
    isCountryValid: boolean;
  }[],
) => {
  const changedCol: string[] = [];
  const { ID1, COMMENTS1, COUNTRY_NAME1, SCOPE1, QTY1, DEAL_QUANTITY1, VOLUME1, NCF_SCOPE1 } =
    colNames;
  const isCommentsModifiedAndNonEmpty = true;
  const isQuantityValid = true;
  const isCountryValid = true;
  for (const key in masterData) {
    if (masterData[ID1] === erroredRow[ID1] && editableColumns.includes(key)) {
      const params = {
        isCommentsModifiedAndNonEmpty,
        isQuantityValid,
        isCountryValid,
        masterData,
        erroredRow,
        changedCol,
        key,
        SCOPE1,
        COMMENTS1,
        differences,
        COUNTRY_NAME1,
        ID1,
        QTY1,
        DEAL_QUANTITY1,
        VOLUME1,
        NCF_SCOPE1,
      };
      processErroredDataForEditableColumns(params);
    }
  }
};

const processErroredDataForEditableColumns = (params: {
  isCommentsModifiedAndNonEmpty: boolean;
  isQuantityValid: boolean;
  isCountryValid: boolean;
  masterData: IEditTableData;
  erroredRow: IEditTableData;
  changedCol: string[];
  key: string;
  SCOPE1: string;
  COMMENTS1: string;
  differences: {
    Id: string | number;
    columnName: string[];
    isCommentsValid: boolean;
    isQuantityValid: boolean;
    isCountryValid: boolean;
  }[];
  COUNTRY_NAME1: string;
  ID1: string;
  QTY1: string;
  DEAL_QUANTITY1: string;
  VOLUME1: string;
  NCF_SCOPE1: string;
}) => {
  const {
    masterData,
    erroredRow,
    changedCol,
    key,
    SCOPE1,
    COMMENTS1,
    differences,
    COUNTRY_NAME1,
    ID1,
    QTY1,
    DEAL_QUANTITY1,
    VOLUME1,
  } = params;
  if (masterData[key] !== erroredRow[key]) {
    changedCol.push(key);
    params.isCommentsModifiedAndNonEmpty =
      masterData[COMMENTS1] !== erroredRow[COMMENTS1] && erroredRow[COMMENTS1] !== '';
    const isDataPresentArray = differences.filter((data) => data.Id === masterData[ID1]);
    if ([QTY1, DEAL_QUANTITY1, VOLUME1].includes(key)) {
      params.isQuantityValid = isQtyValid(erroredRow[key]);
    }
    if ([COUNTRY_NAME1, SCOPE1].includes(key)) {
      params.isCountryValid = erroredRow[COUNTRY_NAME1] !== '';
    }
    // if Id is present, then update the columnName key, else push new data
    if (isDataPresentArray.length > 0) {
      isDataPresentArray[0].columnName = changedCol;
    } else {
      differences.push({
        Id: masterData[ID1],
        columnName: changedCol,
        isCommentsValid: params.isCommentsModifiedAndNonEmpty,
        isQuantityValid: params.isQuantityValid,
        isCountryValid: params.isCountryValid,
      });
    }
  }
};

// Funciton handlePublish - is called when PUBLISH button is clicked
export const handlePublish = async (handlePublishOptions: {
  setIsShowDeploymentToPowerBiMessage: Dispatch<React.SetStateAction<boolean>>;
  setIsPublishTableFailure: Dispatch<React.SetStateAction<boolean>>;
  setIsPublished: Dispatch<React.SetStateAction<boolean>>;
  state: IEditFeatureData;
  ID1: string;
  COMMENTS1: string;
  MARKET1: string;
  BUYSELL1: string;
  QTY1: string;
  DEAL_QUANTITY1: string;
  NCF_CATEGORY1: string;
  SCOPE1: string;
  VOLUME1: string;
  NCF_SCOPE1: string;
  isScopeTableSelected: boolean;
  dispatch: Dispatch<Action>;
  setIsShowEditBtn: Dispatch<React.SetStateAction<boolean>>;
  setLooseChanges: Dispatch<React.SetStateAction<boolean>>;
  setOriginalVal: Dispatch<React.SetStateAction<string | number>>;
  setUpdatedCol: Dispatch<React.SetStateAction<string>>;
  setUpdatedVal: Dispatch<React.SetStateAction<string | number>>;
  setIsShowPublishBtn: Dispatch<React.SetStateAction<boolean>>;
  setIsReadyToPublish: Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: Dispatch<React.SetStateAction<boolean>>;
  setIsEditError: Dispatch<React.SetStateAction<boolean>>;
  setIsDirtyData: Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    setIsShowDeploymentToPowerBiMessage,
    setIsPublishTableFailure,
    setIsPublished,
    state,
    ID1,
    COMMENTS1,
    MARKET1,
    BUYSELL1,
    QTY1,
    DEAL_QUANTITY1,
    NCF_CATEGORY1,
    SCOPE1,
    VOLUME1,
    NCF_SCOPE1,
    isScopeTableSelected,
    dispatch,
    setIsShowEditBtn,
    setLooseChanges,
    setOriginalVal,
    setUpdatedCol,
    setUpdatedVal,
    setIsShowPublishBtn,
    setIsReadyToPublish,
    setIsEdit,
    setIsEditError,
    setIsDirtyData,
  } = handlePublishOptions;
  const finalData = [...state.dirtyData];
  //console statement - final data
  //change column/key names as per the required
  const colnames = {
    ID1,
    COMMENTS1,
    MARKET1,
    BUYSELL1,
    QTY1,
    DEAL_QUANTITY1,
    NCF_CATEGORY1,
    SCOPE1,
    VOLUME1,
    NCF_SCOPE1,
  };
  const publishData: IPublishData[] = getPublishData(colnames, finalData, state);
  // for Americas's scope tables, convert scope value from string to integer.
  setScope(isScopeTableSelected, publishData);
  showSpinner(dispatch);
  const postData = {
    region: state.filteredOptions.region as string,
    tableName: state.tableSelected,
    updatedBy: state.userMail,
    modifiedRecords: publishData,
  };
  const resetTableOptions = {
    setIsShowEditBtn,
    setLooseChanges,
    setOriginalVal,
    setUpdatedCol,
    setUpdatedVal,
    setIsShowPublishBtn,
    setIsReadyToPublish,
    dispatch,
    setIsEdit,
    setIsEditError,
    setIsDirtyData,
  };
  makePublishApi(
    resetTableOptions,
    postData,
    setIsPublished,
    state,
    setIsShowDeploymentToPowerBiMessage,
    setIsPublishTableFailure,
  );
};

const getPublishData = (
  colnames: {
    ID1: string;
    COMMENTS1: string;
    MARKET1: string;
    BUYSELL1: string;
    QTY1: string;
    DEAL_QUANTITY1: string;
    NCF_CATEGORY1: string;
    SCOPE1: string;
    VOLUME1: string;
    NCF_SCOPE1: string;
  },
  finalData: IEditDirtyData[],
  state: IEditFeatureData,
) => {
  const {
    ID1,
    COMMENTS1,
    MARKET1,
    BUYSELL1,
    QTY1,
    DEAL_QUANTITY1,
    NCF_CATEGORY1,
    SCOPE1,
    VOLUME1,
    NCF_SCOPE1,
  } = colnames;
  return finalData.map((rec: IEditDirtyData) => {
    const finalPublishData: IPublishData = {
      id: rec[ID1] as number,
      comments: rec[COMMENTS1] as string,
    };
    const propertiesToMap = [
      { key: 'market', value: MARKET1 },
      { key: 'buySell', value: BUYSELL1 },
      { key: 'qty', value: QTY1 },
      { key: 'qty', value: DEAL_QUANTITY1 },
      { key: 'ncfCategory', value: NCF_CATEGORY1 },
      { key: 'scope', value: SCOPE1 },
      { key: 'countryCode', value: COUNTRY_CODE },
      { key: 'countryName', value: COUNTRY_NAME },
      { key: 'qty', value: VOLUME1 },
      { key: 'scope', value: NCF_SCOPE1 },
    ];
    propertiesToMap.forEach(({ key, value }) => {
      if (rec[value] !== undefined) {
        finalPublishData[key] = rec[value];
      }
    });

    if (state.tableSelected === ENDURAU_STATIC_MAP_NCF) {
      updateFinalPublishData(finalPublishData, state.masterErroredRows);
    }
    return finalPublishData;
  });
};

const updateFinalPublishData = (
  finalPublishData: IPublishData,
  masterErroredRows: IEditTableData[],
): IPublishData | false => {
  const masterRecord = masterErroredRows.find(
    (record: { Id: number }) => record.Id === finalPublishData.Id,
  );

  if (!masterRecord) {
    return false;
  }

  // If finalPublishData has only scope
  if ('scope' in finalPublishData && !('ncfCategory' in finalPublishData)) {
    finalPublishData.ncfCategory = masterRecord.NCF_CATEGORY as string;
  }

  // If finalPublishData has only ncfCategory
  if ('ncfCategory' in finalPublishData && !('scope' in finalPublishData)) {
    finalPublishData.scope = masterRecord.NCF_SCOPE as string;
  }

  return finalPublishData;
};

const setScope = (isScopeTableSelected: boolean, publishData: IPublishData[]) => {
  if (isScopeTableSelected) {
    const scopeMap = {
      [OUT_OF_SCOPE]: 0,
      [IN_SCOPE]: 1,
    };

    for (const obj of publishData) {
      if (obj.scope && obj.scope in scopeMap) {
        obj.scope = scopeMap[obj.scope as keyof typeof scopeMap];
      }
    }
  }
};
const makePublishApi = async (
  resetTableOptions: IEditTableResetOptions,
  postData: IAuditPostData,
  setIsPublished: Dispatch<SetStateAction<boolean>>,
  state: IEditFeatureData,
  setIsShowDeploymentToPowerBiMessage: Dispatch<SetStateAction<boolean>>,
  setIsPublishTableFailure: Dispatch<SetStateAction<boolean>>,
) => {
  const { dispatch } = resetTableOptions;
  try {
    await fetchReport('put', postData);
    setIsPublished(true);
    setTimeout(() => {
      setIsPublished(false);
    }, THOUSAND);
    resetTable(resetTableOptions);
    dispatch({ type: 'setHasTableChangesPublished', payload: true });

    //view or edit report page
    const options = state.filteredOptions;
    const viewReport = await fetchReport('get', options);
    dispatch({ type: 'setHasTableChangesPublished', payload: false });
    if (viewReport?.data) {
      dispatch({
        type: 'setViewEditPaginationData',
        payload: viewReport?.data.records,
      });
      dispatch({
        type: 'setMasterPaginationData',
        payload: JSON.parse(JSON.stringify(viewReport?.data.records)),
      });
      dispatch({ type: 'setRecordCount', payload: viewReport?.data.count });
    }
    hideSpinner(dispatch);
    dispatch({ type: 'setShowReport', payload: true });

    setIsShowDeploymentToPowerBiMessage(true);
    setTimeout(() => {
      setIsShowDeploymentToPowerBiMessage(false);
    }, FIVE_THOUSAND);
  } catch (e) {
    globalApiErrorHandler(ERROR_FETCHING_PPT_DATA, e);
    setIsPublishTableFailure(true);
    hideSpinner(dispatch);
  }
};

// Function handleGetNewRecord - is called when button GET NEW REOCRD is clicked.
// This leverages the user to move out of current table view or table edit scenario
export const handleGetNewRecord = (handleGetNewRecordOptions: IEditTableResetOptionsWithState) => {
  const { state, setLooseChanges } = handleGetNewRecordOptions;
  //If clicked during edit mode or publish mode, alert user to loose unsaved/unpublished data
  if (state.dirtyData.length > 0) {
    setLooseChanges(true);
  } else {
    handleConfirmNewRecord(handleGetNewRecordOptions);
  }
};

// Function resetTable - will reset all the state variables to their default values
export const resetTable = (options: IEditTableResetOptions) => {
  const {
    setIsShowEditBtn,
    setLooseChanges,
    setOriginalVal,
    setUpdatedCol,
    setUpdatedVal,
    setIsShowPublishBtn,
    setIsReadyToPublish,
    dispatch,
    setIsEdit,
    setIsEditError,
    setIsDirtyData,
  } = options;
  //reset the buttons
  setIsShowEditBtn(true);
  setIsShowPublishBtn(false);
  setIsReadyToPublish(false);
  //hide table
  dispatch({ type: 'setShowReport', payload: false });
  //enable Get Data button
  dispatch({ type: 'setIsGetData', payload: false });
  //empty all previos table data, pagination data and dirty data
  dispatch({ type: 'setViewEditPaginationData', payload: [] });
  dispatch({ type: 'setDirtyData', payload: [] });
  dispatch({ type: 'setMasterPaginationData', payload: [] });
  dispatch({ type: 'setErroredRows', payload: [] });
  dispatch({ type: 'setMasterErroredRows', payload: [] });
  dispatch({ type: 'setPublishRows', payload: [] });
  // set editable mode to false
  setIsEdit(false);
  setIsEditError(false);
  // reset variables
  setIsDirtyData(true);
  dispatch({ type: 'setIsAnyInvalidComment', payload: false });
  dispatch({
    type: 'setIsAnyInvalidCountryName',
    payload: false,
  });
  dispatch({ type: 'setIsAnyInvalidQuantity', payload: false });
  setLooseChanges(false);
  setOriginalVal('');
  setUpdatedCol('');
  setUpdatedVal('');
};

export const handleConfirmNewRecord = (handleGetNewRecordOptions: IEditTableResetOptions) => {
  resetTable(handleGetNewRecordOptions);
};

export const handleCancelNewRecord = (setLooseChanges: Dispatch<SetStateAction<boolean>>) => {
  setLooseChanges(false);
};

export const innerOnClose = (
  isPublishTableFailure: boolean,
  setIsServiceFailure: Dispatch<SetStateAction<boolean>>,
  setIsPublishTableFailure: Dispatch<SetStateAction<boolean>>,
) => {
  if (isPublishTableFailure) {
    setIsPublishTableFailure(false);
  }
  setIsServiceFailure(false);
};

export const EmptyComponent = (): ReactElement => (
  <Text>
    <b>There&apos;s no record</b>
  </Text>
);
